import React, { useEffect, useState, useCallback } from "react";
import ProductionModal from "../components/production/ProductionModal";
import PrimaryButton from "../../CommonComponents/PrimaryButton";
import EmptyState from "../../CommonComponents/EmptyState";
import { ChefHat, WifiOff } from "lucide-react";
import { getAllPOSProducts } from "../../../services/posProductServices";
import { fetchProductions } from "../../../services/productionServices";
import { authStorage } from "../../../utils/authStorage";
import SearchInput from "../../CommonComponents/SearchInput";
import Pagination from "../../CommonComponents/Pagination";
import { tableContainer, tableBase, tableHead, tableHeader, tableCell, tableRow } from "../../../utils/tableStyle";

const ProductionPage = () => {
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);
  const [productions, setProductions] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsError, setLogsError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const rowsPerPage = 10;

  const getTodayIso = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchProducts = useCallback(async () => {
    setProductsLoading(true);
    setProductsError(null);
    try {
      const user = authStorage.getUser();
      const branchId = user?.branch_id;
      const response = await getAllPOSProducts(branchId);
      setProducts(Array.isArray(response) ? response : []);
    } catch (err) {
      setProductsError(err);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  const fetchTodayProductions = useCallback(async () => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const today = getTodayIso();
      const response = await fetchProductions({
        page: currentPage,
        limit: rowsPerPage,
        search: searchTerm,
        startDate: today,
        endDate: today,
      });
      setProductions(Array.isArray(response?.data) ? response.data : []);
      setTotalPages(Math.max(Number(response?.pagination?.totalPages) || 1, 1));
      setTotalCount(Number(response?.pagination?.total) || 0);
    } catch (err) {
      setLogsError(err);
    } finally {
      setLogsLoading(false);
    }
  }, [currentPage, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchTodayProductions();
  }, [fetchTodayProductions]);

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString();
  };

  const formatTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="pt-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: "var(--color-text)" }}>Production</h2>
          <p className="text-sm text-gray-500">Today&apos;s production board. Older records are in Production Logs.</p>
        </div>
        <PrimaryButton onClick={() => setModalOpen(true)}>+ Record Production</PrimaryButton>
      </div>

      <div className="mb-4">
        <SearchInput
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          placeholder="Search today's production by product..."
        />
      </div>

      {productsError ? (
        <EmptyState
          icon={WifiOff}
          title="Failed to Load Products"
          message="Check your connection and try again."
          onRetry={fetchProducts}
          retryLabel="Reload"
        />
      ) : productsLoading ? (
        <div className="text-center py-8 text-gray-500">Loading products...</div>
      ) : !products.length ? (
        <EmptyState
          icon={ChefHat}
          title="No Products"
          message="Create and assign products to this branch first."
        />
      ) : (
        <>
          {logsError ? (
            <EmptyState
              icon={WifiOff}
              title="Failed to Load Today's Production"
              message="Check your connection and try again."
              onRetry={fetchTodayProductions}
              retryLabel="Reload"
            />
          ) : logsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading today's production...</div>
          ) : !productions.length ? (
            <EmptyState
              icon={ChefHat}
              title="No Production Today"
              message="Record a production entry to start today&apos;s list."
            />
          ) : (
            <>
              <div className={tableContainer}>
                <table className={tableBase}>
                  <thead className={tableHead} style={{ background: "var(--color-primary)" }}>
                    <tr>
                      <th className={tableHeader + " text-center"}>Date</th>
                      <th className={tableHeader + " text-center"}>Time</th>
                      <th className={tableHeader + " text-center"}>Product</th>
                      <th className={tableHeader + " text-center"}>Qty Produced</th>
                      <th className={tableHeader + " text-center"}>Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productions.map((production) => (
                      <tr key={production.id} className={tableRow + " border-b last:border-0"}>
                        <td className={tableCell + " text-center"}>{formatDate(production.created_at || production.createdAt)}</td>
                        <td className={tableCell + " text-center"}>{formatTime(production.created_at || production.createdAt)}</td>
                        <td className={tableCell + " text-center"}>{production.product?.name || "-"}</td>
                        <td className={tableCell + " text-center tabular-nums"}>{Number(production.qty) || 0}</td>
                        <td className={tableCell + " text-center"}>{production.product?.unit_type || "pcs"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col items-center gap-2 mt-3">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
                <p className="text-sm" style={{ color: "var(--color-text)", opacity: 0.7 }}>
                  Showing {totalCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1} -{" "}
                  {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} items | Page {currentPage} of {totalPages}
                </p>
              </div>
            </>
          )}
        </>
      )}

      <ProductionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        products={products}
        onProductionSaved={() => {
          fetchProducts();
          fetchTodayProductions();
        }}
      />
    </div>
  );
};

export default ProductionPage;
